import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, CircularProgress, Tab, Tabs,
  Select, MenuItem, FormControl, InputLabel, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Autocomplete, Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon, Delete as DeleteIcon,
  PlayArrow as GenerateIcon, Edit as EditIcon, Assignment as AssignIcon,
} from '@mui/icons-material';
import { TareoService } from '../services/tareoService';
import { TurnosService } from '../../turnos/services/turnosService';
import { PlanillonService } from '../../planillon/services/planillonService';
import type { TareoResumen, TareoDiario, TurnoTrabajadorResumen } from '../types/tareo';
import type { PlanillaCabecera } from '../../planillon/types/planillon';
import type { Turno } from '../../turnos/types/turnos';
import AsignarTurnoDialog from './AsignarTurnoDialog';

const DIAS_LABELS: Record<string, string> = {
  '1': 'Lun', '2': 'Mar', '3': 'Mié', '4': 'Jue', '5': 'Vie', '6': 'Sáb', '7': 'Dom',
};

function formatDias(diaSemana: string) {
  return diaSemana.split(',').map(d => DIAS_LABELS[d.trim()] || d).join(', ');
}

function formatTime(t: string | null | undefined) {
  if (!t) return '-';
  return t.substring(0, 5);
}

function calcHoras(entrada: string | null, salida: string | null): number {
  if (!entrada || !salida) return 0;
  const [h1, m1] = entrada.split(':').map(Number);
  const [h2, m2] = salida.split(':').map(Number);
  let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (mins < 0) mins += 24 * 60; // turno nocturno
  return Math.round((mins / 60) * 100) / 100;
}

export default function TareoPage() {
  const [tab, setTab] = useState(0);
  const [planillas, setPlanillas] = useState<PlanillaCabecera[]>([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);

  // Tab 0: Tareo Diario
  const [resumen, setResumen] = useState<TareoResumen[]>([]);
  const [tareos, setTareos] = useState<TareoDiario[]>([]);
  const [viewMode, setViewMode] = useState<'resumen' | 'detalle'>('resumen');
  const [selectedTrabajador, setSelectedTrabajador] = useState<number | null>(null);
  const [selectedTrabajadorNombre, setSelectedTrabajadorNombre] = useState('');

  // Tab 1: Turno x Trabajador
  const [asignaciones, setAsignaciones] = useState<TurnoTrabajadorResumen[]>([]);
  const [asignarDialogOpen, setAsignarDialogOpen] = useState(false);

  // Edit tareo dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTareo, setEditingTareo] = useState<TareoDiario | null>(null);
  const [turnos, setTurnos] = useState<Turno[]>([]);

  useEffect(() => { loadPlanillas(); loadTurnos(); }, []);

  const loadPlanillas = async () => {
    try {
      const data = await PlanillonService.getAll();
      setPlanillas(data);
      if (data.length > 0) setSelectedPeriodo(data[0].idPeriodo);
    } catch { /* handled */ }
  };

  const loadTurnos = async () => {
    try { setTurnos(await TurnosService.getAll()); } catch { /* handled */ }
  };

  // ── Load data based on tab ──
  useEffect(() => {
    if (!selectedPeriodo) return;
    if (tab === 0) {
      if (viewMode === 'resumen') loadResumen();
      else if (selectedTrabajador) loadTareosTrabajador(selectedTrabajador);
    } else if (tab === 1) {
      loadAsignaciones();
    }
  }, [selectedPeriodo, tab, viewMode]);

  const loadResumen = async () => {
    setLoading(true);
    try { setResumen(await TareoService.getResumen(selectedPeriodo)); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const loadTareosTrabajador = async (idTrabajador: number) => {
    setLoading(true);
    try { setTareos(await TareoService.getByTrabajadorPeriodo(idTrabajador, selectedPeriodo)); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const loadTareosAll = async () => {
    setLoading(true);
    try { setTareos(await TareoService.getByPeriodo(selectedPeriodo)); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const loadAsignaciones = async () => {
    setLoading(true);
    try { setAsignaciones(await TareoService.getTurnosTrabajador()); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  // ── Actions ──
  const handleGenerarTareo = async () => {
    if (!selectedPeriodo) return;
    setLoading(true);
    setMessage(null);
    try {
      const result = await TareoService.generarTareo(selectedPeriodo);
      setMessage({ text: result.message, severity: result.count > 0 ? 'success' : 'info' });
      if (viewMode === 'resumen') loadResumen();
      else if (selectedTrabajador) loadTareosTrabajador(selectedTrabajador);
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Error al generar tareo', severity: 'error' });
    } finally { setLoading(false); }
  };

  const handleViewDetalle = (row: TareoResumen) => {
    setSelectedTrabajador(row.idTrabajador);
    setSelectedTrabajadorNombre(row.nombreTrabajador);
    setViewMode('detalle');
    loadTareosTrabajador(row.idTrabajador);
  };

  const handleViewAll = () => {
    setSelectedTrabajador(null);
    setSelectedTrabajadorNombre('');
    setViewMode('detalle');
    loadTareosAll();
  };

  const handleBackToResumen = () => {
    setViewMode('resumen');
    setSelectedTrabajador(null);
    loadResumen();
  };

  const handleDeleteTareo = async (id: number) => {
    if (!confirm('¿Eliminar este registro de tareo?')) return;
    try {
      await TareoService.deleteTareo(id);
      if (selectedTrabajador) loadTareosTrabajador(selectedTrabajador);
      else loadTareosAll();
    } catch { /* handled */ }
  };

  const handleEditTareo = (tareo: TareoDiario) => {
    setEditingTareo({ ...tareo });
    setEditDialogOpen(true);
  };

  const handleSaveTareo = async () => {
    if (!editingTareo) return;
    try {
      await TareoService.saveTareo(editingTareo);
      setEditDialogOpen(false);
      if (selectedTrabajador) loadTareosTrabajador(selectedTrabajador);
      else loadTareosAll();
    } catch { /* handled */ }
  };

  const handleDeleteAsignacion = async (id: number) => {
    if (!confirm('¿Eliminar esta asignación de turno?')) return;
    try {
      await TareoService.deleteTurnoTrabajador(id);
      loadAsignaciones();
    } catch { /* handled */ }
  };

  // ── Columns ──
  const resumenColumns: GridColDef[] = [
    { field: 'nombreTrabajador', headerName: 'Trabajador', flex: 1, minWidth: 250 },
    { field: 'diasRegistrados', headerName: 'Días', width: 90, type: 'number' },
    { field: 'totalHoras', headerName: 'Total Horas', width: 120,
      valueFormatter: (v: number) => v.toFixed(2) },
    { field: 'acciones', headerName: '', width: 80, sortable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => handleViewDetalle(p.row)}>Ver</Button>
      ) },
  ];

  const tareoColumns: GridColDef[] = [
    ...(selectedTrabajador ? [] : [{
      field: 'trabajadorNombre', headerName: 'Trabajador', flex: 1, minWidth: 200,
      valueGetter: (_v: unknown, row: TareoDiario) =>
        row.trabajador?.personal?.personaContacto ?? `#${row.idTrabajador}`,
    } as GridColDef]),
    { field: 'fecha', headerName: 'Fecha', width: 120,
      valueFormatter: (v: string) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'turnoDesc', headerName: 'Turno', width: 150,
      valueGetter: (_v: unknown, row: TareoDiario) => row.turno?.descripcion ?? '-' },
    { field: 'horaEntrada', headerName: 'Entrada', width: 80,
      valueFormatter: (v: string | null) => formatTime(v) },
    { field: 'horaSalida', headerName: 'Salida', width: 80,
      valueFormatter: (v: string | null) => formatTime(v) },
    { field: 'horasTrabajadas', headerName: 'Horas', width: 80, type: 'number',
      valueFormatter: (v: number) => v.toFixed(2) },
    { field: 'codigoCentroCosto', headerName: 'CC', width: 100 },
    { field: 'observaciones', headerName: 'Obs.', flex: 1, minWidth: 100 },
    { field: 'acciones', headerName: '', width: 90, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0}>
          <IconButton size="small" onClick={() => handleEditTareo(p.row)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteTareo(p.row.idTareo)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) },
  ];

  const asignacionColumns: GridColDef[] = [
    { field: 'nombreTrabajador', headerName: 'Trabajador', flex: 1, minWidth: 200 },
    { field: 'turnoDescripcion', headerName: 'Turno', width: 180 },
    { field: 'fechaDesde', headerName: 'Desde', width: 120,
      valueFormatter: (v: string) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'fechaHasta', headerName: 'Hasta', width: 120,
      valueFormatter: (v: string) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'diaSemana', headerName: 'Días', width: 180,
      renderCell: (p) => formatDias(p.value) },
    { field: 'activo', headerName: 'Activo', width: 80, type: 'boolean' },
    { field: 'acciones', headerName: '', width: 60, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={() => handleDeleteAsignacion(p.row.idTurnoTrabajador)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Tareo Diario</Typography>

      {/* ── Period selector (shared) ── */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 350 }}>
          <InputLabel>Periodo</InputLabel>
          <Select value={selectedPeriodo || ''} label="Periodo"
            onChange={(e) => { setSelectedPeriodo(Number(e.target.value)); setViewMode('resumen'); }}>
            {planillas.map(p => (
              <MenuItem key={p.idPlanilla} value={p.idPeriodo}>{p.descripcion}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Tareo Diario" />
        <Tab label="Turno x Trabajador" />
      </Tabs>

      {/* ── TAB 0: Tareo Diario ── */}
      {tab === 0 && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Tooltip title="Genera registros de tareo automáticamente a partir de las asignaciones Turno x Trabajador">
              <Button variant="contained" color="success" startIcon={<GenerateIcon />}
                onClick={handleGenerarTareo} disabled={loading || !selectedPeriodo}>
                Generar Tareo
              </Button>
            </Tooltip>
            {viewMode === 'resumen' ? (
              <Button variant="outlined" onClick={handleViewAll}>Ver Todo Detalle</Button>
            ) : (
              <Button variant="outlined" onClick={handleBackToResumen}>Volver a Resumen</Button>
            )}
            <Button variant="outlined" startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              onClick={() => viewMode === 'resumen' ? loadResumen() : (selectedTrabajador ? loadTareosTrabajador(selectedTrabajador) : loadTareosAll())}
              disabled={loading}>
              Actualizar
            </Button>
          </Stack>

          {viewMode === 'resumen' ? (
            <DataGrid
              rows={resumen.map((r, i) => ({ ...r, _id: i }))}
              columns={resumenColumns}
              getRowId={(row) => row._id}
              autoHeight density="compact" disableRowSelectionOnClick
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            />
          ) : (
            <>
              {selectedTrabajador && (
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Detalle de: <strong>{selectedTrabajadorNombre}</strong>
                </Typography>
              )}
              <DataGrid
                rows={tareos}
                columns={tareoColumns}
                getRowId={(row) => row.idTareo}
                autoHeight density="compact" disableRowSelectionOnClick
                pageSizeOptions={[25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                  sorting: { sortModel: [{ field: 'fecha', sort: 'asc' }] },
                }}
              />
            </>
          )}
        </>
      )}

      {/* ── TAB 1: Turno x Trabajador ── */}
      {tab === 1 && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AssignIcon />}
              onClick={() => setAsignarDialogOpen(true)}>
              Asignar Turno
            </Button>
            <Button variant="outlined" startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              onClick={loadAsignaciones} disabled={loading}>
              Actualizar
            </Button>
          </Stack>
          <DataGrid
            rows={asignaciones}
            columns={asignacionColumns}
            getRowId={(row) => row.idTurnoTrabajador}
            autoHeight density="compact" disableRowSelectionOnClick
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </>
      )}

      {/* ── Dialog: Asignar Turno ── */}
      <AsignarTurnoDialog
        open={asignarDialogOpen}
        onClose={() => setAsignarDialogOpen(false)}
        onSuccess={loadAsignaciones}
      />

      {/* ── Dialog: Editar Tareo ── */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar Tareo</DialogTitle>
        <DialogContent>
          {editingTareo && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Fecha" type="date" size="small" fullWidth
                value={editingTareo.fecha?.substring(0, 10) ?? ''}
                onChange={(e) => setEditingTareo({ ...editingTareo, fecha: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }} />
              <Autocomplete
                options={turnos.filter(t => t.activo)}
                getOptionLabel={(o) => `${o.descripcion} (${o.horaInicio?.substring(0, 5)}-${o.horaFin?.substring(0, 5)})`}
                value={turnos.find(t => t.idTurno === editingTareo.idTurno) ?? null}
                onChange={(_, v) => {
                  const ent = v?.horaInicio ?? editingTareo?.horaEntrada;
                  const sal = v?.horaFin ?? editingTareo?.horaSalida;
                  setEditingTareo(prev => prev ? {
                    ...prev,
                    idTurno: v?.idTurno ?? null,
                    horaEntrada: ent,
                    horaSalida: sal,
                    horasTrabajadas: calcHoras(ent, sal),
                  } : prev);
                }}
                renderInput={(p) => <TextField {...p} label="Turno" size="small" />}
              />
              <Stack direction="row" spacing={2}>
                <TextField label="Entrada" type="time" size="small" fullWidth
                  value={formatTime(editingTareo.horaEntrada)}
                  onChange={(e) => {
                    const ent = e.target.value + ':00';
                    setEditingTareo(prev => prev ? { ...prev, horaEntrada: ent, horasTrabajadas: calcHoras(ent, prev.horaSalida) } : prev);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }} />
                <TextField label="Salida" type="time" size="small" fullWidth
                  value={formatTime(editingTareo.horaSalida)}
                  onChange={(e) => {
                    const sal = e.target.value + ':00';
                    setEditingTareo(prev => prev ? { ...prev, horaSalida: sal, horasTrabajadas: calcHoras(prev.horaEntrada, sal) } : prev);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }} />
              </Stack>
              <TextField label="Horas Trabajadas" type="number" size="small"
                value={editingTareo.horasTrabajadas}
                onChange={(e) => setEditingTareo({ ...editingTareo, horasTrabajadas: Number(e.target.value) })}
                inputProps={{ min: 0, step: 0.5 }} />
              <TextField label="Centro de Costo" size="small"
                value={editingTareo.codigoCentroCosto ?? ''}
                onChange={(e) => setEditingTareo({ ...editingTareo, codigoCentroCosto: e.target.value || null })} />
              <TextField label="Observaciones" size="small" multiline rows={2}
                value={editingTareo.observaciones ?? ''}
                onChange={(e) => setEditingTareo({ ...editingTareo, observaciones: e.target.value || null })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveTareo}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
